// Autonomous lead screen-pop: opens the instant `dialer:lead-answered` fires.
// Shows lead facts, a live call timer, an optional disposition selector, notes and End Call.
// Dispositions are optional: X / Save & Continue never block on one.
import React, { useEffect, useState } from 'react';
import { Phone, PhoneOff, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiPost } from '../../lib/api.js';
import { formatPhone } from './powerDialConstants.js';

const DISPOSITIONS = [
  { label: 'Interested', outcome: 'connected' },
  { label: 'Set Appointment', outcome: 'booked' },
  { label: 'Not Interested', outcome: 'not-interested' },
  { label: 'Callback', outcome: 'callback' },
];

function fmtTimer(sec) {
  const m = String(Math.floor(sec / 60)).padStart(2, '0');
  const s = String(sec % 60).padStart(2, '0');
  return `${m}:${s}`;
}

function ageOf(lead) {
  if (lead?.birthday) {
    const d = new Date(lead.birthday);
    if (!Number.isNaN(d.getTime())) {
      return Math.floor((Date.now() - d.getTime()) / (365.25 * 24 * 3600 * 1000));
    }
  }
  return lead?.age || lead?.ageRange || '';
}

/**
 * @param {{
 *   pop: {leadData?: object, callId?: string, callSid?: string, ended?: boolean},
 *   onClose: () => void,
 *   onEndCall?: (callSid?: string) => Promise<unknown>|void,
 *   onDirty?: () => void,
 * }} props
 *   onEndCall hangs up the live call; onDirty fires once the agent picks a
 *   disposition or types notes (so an ended call keeps the box open for them).
 */
export default function PowerDialScreenPop({ pop, onClose, onEndCall, onDirty }) {
  const { leadData: lead = {}, callId, callSid, ended = false } = pop;
  const [elapsed, setElapsed] = useState(0);
  const [outcome, setOutcome] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [ending, setEnding] = useState(false);

  // Timer restarts at 00:00 for each new answered call.
  useEffect(() => {
    setElapsed(0);
    setOutcome('');
    setNotes('');
  }, [callSid]);
  // ...and stops once the call is over.
  useEffect(() => {
    if (ended) return undefined;
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, [callSid, ended]);

  const pick = (o) => { setOutcome(o); onDirty?.(); };
  const typeNotes = (v) => { setNotes(v); if (v.trim()) onDirty?.(); };

  /** Hang up the live call. Resolves false when the hang-up FAILED (call still live). */
  const endCall = async () => {
    if (ended || ending || !onEndCall) return true;
    setEnding(true);
    try { await onEndCall(callSid); return true; } catch (_) { return false; /* the run bar already toasted */ } finally { setEnding(false); }
  };

  // Ends the call if still live, saves the disposition only if one was picked, closes.
  const save = async () => {
    setSaving(true);
    try {
      // Never close the box over a call that is still live — keep it so End Call can be retried.
      if (!(await endCall())) return;
      if (outcome && callId) {
        await apiPost(`/phone/calls/${callId}/disposition`, { outcome, notes });
        toast.success('Disposition saved');
      }
      window.dispatchEvent(new CustomEvent('civix:disposition:done'));
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save disposition');
    } finally {
      setSaving(false);
    }
  };

  const name = `${lead.firstName || ''} ${lead.lastName || ''}`.trim() || 'Lead';
  const facts = [
    ['Phone', formatPhone(lead.phone)],
    ['State', lead.state],
    ['Coverage requested', lead.mortgageAmount],
    ['Policy type', (lead.vertical || '').replace(/_/g, ' ')],
    ['Age / DOB', ageOf(lead)],
  ];
  const notesList = Array.isArray(lead.notes) ? lead.notes.slice(-3).reverse() : [];

  return (
    <aside
      role="dialog"
      aria-label="Answered lead"
      className="fixed top-4 left-4 z-950 w-[380px] max-w-[calc(100vw-2rem)] max-h-[calc(100vh-8rem)] overflow-y-auto rounded-2xl bg-[#0B0C14] border border-emerald-500/50 shadow-[0_12px_40px_rgba(0,0,0,0.7)] text-white"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-emerald-500/10">
        <div className="flex items-center gap-2">
          <Phone className="w-4 h-4 text-emerald-300" />
          <span className="font-bold">{name}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="font-mono text-emerald-300 text-sm" aria-label="Call duration">{ended ? 'Ended' : fmtTimer(elapsed)}</span>
          {!ended && onEndCall && (
            <button
              type="button"
              onClick={endCall}
              disabled={ending}
              data-testid="pop-end-call"
              className="flex items-center gap-1 rounded-lg bg-red-600 px-2 py-1 text-xs font-bold text-white hover:bg-red-500 disabled:opacity-60"
            >
              <PhoneOff className="w-3.5 h-3.5" /> {ending ? 'Ending…' : 'End Call'}
            </button>
          )}
          <button type="button" onClick={onClose} aria-label="Close" data-testid="pop-close" className="text-white/50 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
      </div>

      <dl className="grid grid-cols-2 gap-x-3 gap-y-2 px-4 py-3 text-xs">
        {facts.map(([k, v]) => (
          <div key={k}>
            <dt className="text-white/40 uppercase tracking-wider text-[10px]">{k}</dt>
            <dd className="text-white/90">{v || '—'}</dd>
          </div>
        ))}
      </dl>

      {notesList.length > 0 && (
        <div className="px-4 pb-2">
          <div className="text-white/40 uppercase tracking-wider text-[10px] mb-1">Existing notes</div>
          <ul className="space-y-1 text-xs text-white/80">
            {notesList.map((n, i) => <li key={i} className="bg-white/5 rounded px-2 py-1">{n.body || String(n)}</li>)}
          </ul>
        </div>
      )}

      <div className="px-4 pb-4 space-y-2">
        <div className="grid grid-cols-2 gap-2">
          {DISPOSITIONS.map((d) => (
            <button
              key={d.outcome}
              type="button"
              aria-pressed={outcome === d.outcome}
              onClick={() => pick(outcome === d.outcome ? '' : d.outcome)}
              className={`py-1.5 rounded-lg text-xs font-semibold border transition ${
                outcome === d.outcome
                  ? 'bg-civix-purple text-white border-civix-purple'
                  : 'border-white/10 bg-white/5 text-white/70 hover:border-civix-purple/40'
              }`}
            >
              {d.label}
            </button>
          ))}
        </div>
        <textarea
          value={notes}
          onChange={(e) => typeNotes(e.target.value)}
          rows={4}
          placeholder="Call notes (optional)…"
          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-civix-purple"
        />
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="w-full py-2 rounded-lg text-sm font-bold bg-linear-to-r from-[#7C3AED] to-[#E040FB] disabled:opacity-60"
        >
          {saving ? 'Saving…' : ended || !onEndCall ? 'Save & Continue' : 'End Call & Continue'}
        </button>
      </div>
    </aside>
  );
}
